// src/pages/HechosAdmin.jsx
//
// Administración del módulo Hechos Operativos. Solo admin. Es la pantalla que
// desbloquea el uso real: hasta aquí el módulo lo ve solo el admin y todo hecho
// cae "sin asignar". Con esto se designan abogados, se reparten zonas, se fija
// el de guardia y se marcan los puestos que pueden levantar.
//
// Guía el orden correcto con las pestañas: Abogados → Zonas → Acceso → Tipos.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
    getAdminConfig, searchUsers, updatePolicy, setLawyer, updateZone,
} from '../services/incidentApi';
import { HechosStyles, Membrete, I, Pill } from '../components/hechos/ui';

const TABS = [
    { key: 'abogados', label: 'Abogados' },
    { key: 'zonas',    label: 'Zonas' },
    { key: 'acceso',   label: 'Acceso y guardia' },
    { key: 'tipos',    label: 'Tipos de evento' },
];

export default function HechosAdmin() {
    const navigate = useNavigate();
    const [cfg, setCfg] = useState(null);
    const [tab, setTab] = useState('abogados');
    const [cargando, setCargando] = useState(true);

    const cargar = useCallback(async () => {
        try { setCfg(await getAdminConfig()); }
        catch (e) {
            if (e.response?.status === 403) { toast.error('Solo un administrador puede configurar el módulo'); navigate('/hechos'); }
            else toast.error('No se pudo cargar la configuración');
        } finally { setCargando(false); }
    }, [navigate]);

    useEffect(() => { cargar(); }, [cargar]);

    if (cargando) return <div className="ho-wrap"><HechosStyles /><div className="ho-empty">Cargando configuración…</div></div>;
    if (!cfg) return null;

    return (
        <div className="ho-wrap">
            <HechosStyles />
            <Membrete />

            <div className="d-flex align-items-start flex-wrap gap-3 pt-2">
                <div>
                    <div className="ho-kicker">Administración</div>
                    <h1 className="ho-h1">Configurar Hechos Operativos</h1>
                    <div className="ho-head-sub">
                        <span>Define quién atiende, quién levanta y cómo se reparten los casos</span>
                    </div>
                </div>
                <button className="ho-chip ms-auto" onClick={() => navigate('/hechos')}>Ver registro</button>
            </div>

            <ListaPendientes cfg={cfg} />

            <div className="ho-filters" role="tablist" style={{ marginTop: 10 }}>
                {TABS.map(t => (
                    <button key={t.key} className="ho-chip" aria-pressed={tab === t.key}
                            onClick={() => setTab(t.key)}>{t.label}</button>
                ))}
            </div>

            {tab === 'abogados' && <Abogados cfg={cfg} recargar={cargar} />}
            {tab === 'zonas'    && <Zonas cfg={cfg} recargar={cargar} />}
            {tab === 'acceso'   && <Acceso cfg={cfg} recargar={cargar} />}
            {tab === 'tipos'    && <Tipos cfg={cfg} />}
        </div>
    );
}

/** Semáforo de lo que falta para que el módulo opere solo. */
function ListaPendientes({ cfg }) {
    const hayAbogados = cfg.abogados.length > 0;
    const hayGuardia  = !!cfg.policy.abogadoGuardia;
    const zonasActivas = cfg.zonas.filter(z => z.active).length;
    const hayPuestos  = (cfg.policy.puestosElegibles || []).length > 0 ||
                        (cfg.policy.usuariosPermitidos || []).length > 0;

    const items = [
        ['Al menos un abogado designado', hayAbogados],
        ['Abogado de guardia definido', hayGuardia],
        ['Al menos una zona activa', zonasActivas > 0],
        ['Puestos que pueden levantar', hayPuestos],
    ];
    const listo = items.every(i => i[1]);

    if (listo) return (
        <div className="ho-focus" style={{ marginTop: 18, background: 'color-mix(in srgb, var(--ho-ok) 10%, var(--c-card))', borderColor: 'color-mix(in srgb, var(--ho-ok) 34%, transparent)' }}>
            <span style={{ color: 'var(--ho-ok)', marginTop: 1 }}>{I.check}</span>
            <div><div className="t" style={{ color: 'var(--ho-ok)' }}>El módulo está configurado</div>
                 <div className="d">Los gerentes pueden levantar hechos y los casos se asignan automáticamente.</div></div>
        </div>
    );

    return (
        <div className="ho-focus" style={{ marginTop: 18 }}>
            <span style={{ color: 'var(--ho-pend)', marginTop: 1 }}>{I.alert}</span>
            <div style={{ flex: 1 }}>
                <div className="t">Configuración pendiente para que el módulo opere de forma autónoma</div>
                <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {items.map(([label, ok]) => (
                        <div key={label} style={{ fontSize: 13, color: 'var(--c-text-2)', display: 'flex', gap: 8, alignItems: 'center' }}>
                            <span style={{ color: ok ? 'var(--ho-ok)' : 'var(--c-text-4)' }}>
                                {ok ? I.check : I.alert}
                            </span>
                            <span style={{ textDecoration: ok ? 'line-through' : 'none', opacity: ok ? .6 : 1 }}>{label}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

// ── Buscador de usuarios reutilizable ────────────────────────────────────
function BuscadorUsuario({ onElegir, placeholder = 'Buscar por nombre o correo…' }) {
    const [q, setQ] = useState('');
    const [res, setRes] = useState([]);
    const [abierto, setAbierto] = useState(false);

    useEffect(() => {
        if (!q.trim()) { setRes([]); return; }
        const t = setTimeout(() => {
            searchUsers(q).then(r => { setRes(r.users); setAbierto(true); }).catch(() => {});
        }, 280);
        return () => clearTimeout(t);
    }, [q]);

    return (
        <div style={{ position: 'relative', maxWidth: 420 }}>
            <input className="form-control form-control-sm" value={q} placeholder={placeholder}
                   onChange={e => setQ(e.target.value)} onFocus={() => res.length && setAbierto(true)} />
            {abierto && res.length > 0 && (
                <div style={{ position: 'absolute', zIndex: 20, top: '100%', left: 0, right: 0, marginTop: 4,
                              background: 'var(--c-card)', border: '1px solid var(--c-border)', borderRadius: 8,
                              boxShadow: '0 8px 24px -8px rgba(0,0,0,.3)', maxHeight: 260, overflowY: 'auto' }}>
                    {res.map(u => (
                        <button key={u._id} className="btn btn-link text-start w-100 px-3 py-2"
                                style={{ textDecoration: 'none', color: 'var(--c-text-1)', borderBottom: '1px solid var(--c-border)' }}
                                onClick={() => { onElegir(u); setQ(''); setRes([]); setAbierto(false); }}>
                            <div style={{ fontSize: 13.5, fontWeight: 600 }}>{u.name}</div>
                            <div style={{ fontSize: 12, color: 'var(--c-text-3)' }}>{[u.email, u.puesto].filter(Boolean).join(' · ')}</div>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

const card = { background: 'var(--c-card)', border: '1px solid var(--c-border)', borderRadius: 8, padding: '18px 20px', marginTop: 16 };

// ── Abogados ─────────────────────────────────────────────────────────────
const ROLES_LEGALES = [
    { v: 'abogado',     t: 'Abogado' },
    { v: 'coordinador', t: 'Coordinador' },
    { v: 'director',    t: 'Dirección (abogado jefe)' },
];

function Abogados({ cfg, recargar }) {
    async function designar(u) {
        try { await setLawyer(u._id, { esAbogado: true }); toast.success(`${u.name} designado abogado`); recargar(); }
        catch (e) { toast.error(e.response?.data?.error || 'No se pudo designar'); }
    }
    async function retirar(u) {
        if (!window.confirm(`¿Retirar a ${u.name} como abogado? Perderá sus zonas asignadas.`)) return;
        try { await setLawyer(u._id, { esAbogado: false }); toast.success('Retirado'); recargar(); }
        catch (e) { toast.error(e.response?.data?.error || 'No se pudo retirar'); }
    }
    async function cambiar(u, patch) {
        try { await setLawyer(u._id, patch); recargar(); }
        catch (e) { toast.error(e.response?.data?.error || 'No se pudo actualizar la jerarquía'); }
    }

    const coordinadores = cfg.abogados.filter(a => a.legalRole === 'coordinador');

    // Zonas por abogado desde cfg.zonas (titular o suplente), que es donde la
    // pestaña Zonas realmente guarda; user.legalZones no se actualiza desde ahí.
    const zonasDe = (id) => cfg.zonas.filter(z =>
        String(z.titular?._id || '') === String(id) || String(z.suplente?._id || '') === String(id)
    ).length;

    return (
        <div style={card}>
            <div className="viz-title" style={{ fontSize: 14, fontWeight: 600 }}>Abogados de BJM</div>
            <p style={{ fontSize: 12.5, color: 'var(--c-text-3)', marginTop: 2 }}>
                Empieza por aquí: hasta que alguien es abogado, no hay a quién asignarle zonas ni guardia. Un abogado ve la bitácora legal y atiende expedientes, pero no accede al resto de CaprepaDesk.
            </p>
            <p style={{ fontSize: 12.5, color: 'var(--c-text-3)', marginTop: 6 }}>
                La jerarquía define el alcance: un <strong>abogado</strong> ve solo sus expedientes; un <strong>coordinador</strong> además ve, reasigna y aprueba lo de los abogados que le reportan; la <strong>dirección</strong> gestiona todo el registro.
            </p>
            <div style={{ marginTop: 12 }}>
                <BuscadorUsuario onElegir={designar} placeholder="Buscar usuario para designar abogado…" />
            </div>
            {cfg.abogados.length === 0 ? (
                <div className="ho-empty" style={{ padding: '24px 0' }}>Todavía no hay abogados designados.</div>
            ) : (
                <table className="viz-tabla" style={{ marginTop: 16 }}>
                    <thead><tr><th>Abogado</th><th>Correo</th><th>Rol</th><th>Reporta a</th><th>Zonas</th><th></th></tr></thead>
                    <tbody>
                        {cfg.abogados.map(a => {
                            const rol = a.legalRole || 'abogado';
                            return (
                                <tr key={a._id}>
                                    <td style={{ fontWeight: 600 }}>{a.name}</td>
                                    <td style={{ fontSize: 12.5, color: 'var(--c-text-3)' }}>{a.email || '—'}</td>
                                    <td>
                                        <select className="form-select form-select-sm" value={rol}
                                                onChange={e => cambiar(a, { legalRole: e.target.value })}>
                                            {ROLES_LEGALES.map(r => <option key={r.v} value={r.v}>{r.t}</option>)}
                                        </select>
                                    </td>
                                    <td>
                                        {rol === 'abogado' ? (
                                            <select className="form-select form-select-sm" value={a.legalCoordinator || ''}
                                                    onChange={e => cambiar(a, { legalCoordinator: e.target.value || null })}>
                                                <option value="">— sin coordinador —</option>
                                                {coordinadores.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
                                            </select>
                                        ) : (
                                            <span style={{ fontSize: 12.5, color: 'var(--c-text-3)' }}>—</span>
                                        )}
                                    </td>
                                    <td>{zonasDe(a._id)}</td>
                                    <td style={{ textAlign: 'right' }}>
                                        <button className="ho-chip" onClick={() => retirar(a)}>Retirar</button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            )}
        </div>
    );
}

// ── Zonas ────────────────────────────────────────────────────────────────
function Zonas({ cfg, recargar }) {
    const abogados = cfg.abogados;
    const opciones = useMemo(() => abogados.map(a => ({ v: a._id, t: a.name })), [abogados]);

    async function guardar(zona, patch) {
        try { await updateZone(zona._id, patch); recargar(); }
        catch (e) { toast.error(e.response?.data?.error || 'No se pudo guardar la zona'); }
    }

    if (!abogados.length) return (
        <div style={card}>
            <div className="ho-empty" style={{ padding: '20px 0' }}>
                Primero designa abogados en la pestaña <strong>Abogados</strong>: sin ellos no hay a quién asignar las zonas.
            </div>
        </div>
    );

    return (
        <div style={card}>
            <div className="viz-title" style={{ fontSize: 14, fontWeight: 600 }}>Zonas por estado</div>
            <p style={{ fontSize: 12.5, color: 'var(--c-text-3)', marginTop: 2 }}>
                Cada hecho se asigna al abogado del estado donde ocurrió. Los estados sin zona activa caen al abogado de guardia.
            </p>
            <div className="tbl-wrap" style={{ overflowX: 'auto', marginTop: 14 }}>
                <table className="viz-tabla" style={{ minWidth: 720 }}>
                    <thead><tr><th>Estado</th><th>Titular</th><th>Suplente</th><th>Activa</th></tr></thead>
                    <tbody>
                        {cfg.zonas.map(z => (
                            <tr key={z._id}>
                                <td style={{ fontWeight: 600 }}>{z.name}</td>
                                <td>
                                    <select className="form-select form-select-sm" value={z.titular?._id || ''}
                                            onChange={e => guardar(z, { titular: e.target.value || null })}>
                                        <option value="">— sin titular —</option>
                                        {opciones.map(o => <option key={o.v} value={o.v}>{o.t}</option>)}
                                    </select>
                                </td>
                                <td>
                                    <select className="form-select form-select-sm" value={z.suplente?._id || ''}
                                            onChange={e => guardar(z, { suplente: e.target.value || null })}>
                                        <option value="">— sin suplente —</option>
                                        {opciones.map(o => <option key={o.v} value={o.v}>{o.t}</option>)}
                                    </select>
                                </td>
                                <td>
                                    <div className="form-check form-switch">
                                        <input className="form-check-input" type="checkbox" checked={!!z.active}
                                               onChange={e => guardar(z, { active: e.target.checked })} />
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ── Acceso y guardia ─────────────────────────────────────────────────────
function Acceso({ cfg, recargar }) {
    const [pol, setPol] = useState(cfg.policy);
    const [dominio, setDominio] = useState('');
    const [dirCorreo, setDirCorreo] = useState('');
    const [buscaPuesto, setBuscaPuesto] = useState('');
    const abogados = cfg.abogados;

    const dirActual = pol.enrutamiento?.siempre || [];
    const agregarDir = () => {
        const email = dirCorreo.trim().toLowerCase();
        if (!email) return;
        if (dirActual.includes(email)) { setDirCorreo(''); return; }
        guardar({ enrutamientoSiempre: [...dirActual, email] });
        setDirCorreo('');
    };

    // El intranet tiene cientos de puestos; sin filtro es imposible encontrar el
    // correcto. La comparación va normalizada (sin acentos, minúsculas): buscar
    // "juridico" debe encontrar "Auxiliar Jurídico".
    const puestosFiltrados = useMemo(() => {
        const norm = (s) => String(s || '').toLowerCase()
            .normalize('NFD').replace(/\p{M}/gu, '').trim();
        const q = norm(buscaPuesto);
        return (cfg.puestos || []).filter(p => !q || norm(p).includes(q));
    }, [cfg.puestos, buscaPuesto]);

    async function guardar(patch) {
        try { const r = await updatePolicy(patch); setPol(r.policy); toast.success('Guardado'); recargar(); }
        catch (e) { toast.error(e.response?.data?.error || 'No se pudo guardar'); }
    }

    const togglePuesto = (p) => {
        const set = new Set(pol.puestosElegibles || []);
        set.has(p) ? set.delete(p) : set.add(p);
        guardar({ puestosElegibles: [...set] });
    };

    return (
        <>
            <div style={card}>
                <div className="viz-title" style={{ fontSize: 14, fontWeight: 600 }}>Abogado de guardia</div>
                <p style={{ fontSize: 12.5, color: 'var(--c-text-3)', marginTop: 2 }}>
                    Recibe todo hecho de un estado sin zona activa. Es lo que garantiza que ningún expediente se quede sin dueño.
                </p>
                <div className="row g-3 mt-1" style={{ maxWidth: 640 }}>
                    <div className="col-md-6">
                        <label className="ho-campo-k d-block mb-1">Abogado de guardia</label>
                        <select className="form-select form-select-sm" value={pol.abogadoGuardia || ''}
                                onChange={e => guardar({ abogadoGuardia: e.target.value || null })}>
                            <option value="">— sin definir —</option>
                            {abogados.map(a => <option key={a._id} value={a._id}>{a.name}</option>)}
                        </select>
                    </div>
                    <div className="col-md-6">
                        <label className="ho-campo-k d-block mb-1">Coordinador legal (recibe avisos de guardia)</label>
                        <select className="form-select form-select-sm" value={pol.coordinadorLegal || ''}
                                onChange={e => guardar({ coordinadorLegal: e.target.value || null })}>
                            <option value="">— sin definir —</option>
                            {abogados.map(a => <option key={a._id} value={a._id}>{a.name}</option>)}
                        </select>
                    </div>
                </div>
                {!abogados.length && (
                    <p style={{ fontSize: 12.5, color: 'var(--ho-pend)', marginTop: 10 }}>
                        Designa abogados primero para poder elegir guardia.
                    </p>
                )}
            </div>

            <div style={card}>
                <div className="viz-title" style={{ fontSize: 14, fontWeight: 600 }}>Puestos que pueden levantar</div>
                <p style={{ fontSize: 12.5, color: 'var(--c-text-3)', marginTop: 2 }}>
                    Marca los puestos del intranet cuyos titulares pueden levantar un hecho. La comparación es exacta.
                </p>
                {cfg.puestos?.length ? (
                    <>
                        <div style={{ position: 'relative', maxWidth: 420, marginTop: 12 }}>
                            <span style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: 'var(--c-text-3)', pointerEvents: 'none' }}>{I.search}</span>
                            <input className="form-control form-control-sm" value={buscaPuesto}
                                   onChange={e => setBuscaPuesto(e.target.value)}
                                   placeholder="Buscar puesto…" style={{ paddingLeft: 34 }} />
                        </div>
                        <div style={{ marginTop: 12, maxHeight: 280, overflowY: 'auto', display: 'grid',
                                      gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 6 }}>
                            {puestosFiltrados.map(p => {
                                const on = (pol.puestosElegibles || []).includes(p);
                                return (
                                    <label key={p} className="d-flex align-items-center gap-2" style={{ fontSize: 13, cursor: 'pointer' }}>
                                        <input type="checkbox" className="form-check-input mt-0" checked={on} onChange={() => togglePuesto(p)} />
                                        <span>{p}</span>
                                    </label>
                                );
                            })}
                            {!puestosFiltrados.length && (
                                <p style={{ fontSize: 12.5, color: 'var(--c-text-3)', gridColumn: '1 / -1', margin: 0 }}>
                                    Ningún puesto coincide con “{buscaPuesto}”.
                                </p>
                            )}
                        </div>
                    </>
                ) : (
                    <div style={{ marginTop: 10 }}>
                        <p style={{ fontSize: 12.5, color: 'var(--c-text-3)', marginBottom: 8 }}>
                            No se pudo leer el catálogo de puestos del intranet. Usa el permiso individual de abajo mientras tanto.
                        </p>
                        <button className="ho-chip" onClick={recargar}>Reintentar la carga del catálogo</button>
                    </div>
                )}
                {(pol.puestosElegibles || []).length > 0 && (
                    <div className="d-flex flex-wrap gap-2 mt-3">
                        {pol.puestosElegibles.map(p => <Pill key={p} tone="info">{p}</Pill>)}
                    </div>
                )}
            </div>

            <ListaUsuarios titulo="Permitir levantar a personas específicas"
                sub="Para gerentes cuyo puesto no está en la lista, o para habilitarte a ti mismo y probar."
                ids={pol.usuariosPermitidos} campo="usuariosPermitidos" pol={pol} guardar={guardar} dic={cfg.usuariosNombres || {}} />

            <ListaUsuarios titulo="Recursos Humanos"
                sub="Acceso de consulta y seguimiento a los hechos, sin poder levantar y sin ver la bitácora legal."
                ids={pol.usuariosRH} campo="usuariosRH" pol={pol} guardar={guardar} dic={cfg.usuariosNombres || {}} />

            <ListaUsuarios titulo="Administradores del módulo"
                sub="Configuran abogados, zonas y accesos, y gestionan todo el registro (incluida la bitácora) sin ser administradores de CaprepaDesk. Puede ser cualquier usuario del staff, agentes incluidos."
                ids={pol.adminsHO} campo="adminsHO" pol={pol} guardar={guardar} dic={cfg.usuariosNombres || {}} />

            <ListaUsuarios titulo="Observadores"
                sub="Ven todos los expedientes en solo lectura: sin bitácora legal, sin escribir, sin subir documentos. Para dirección general o auditoría."
                ids={pol.observadores} campo="observadores" pol={pol} guardar={guardar} dic={cfg.usuariosNombres || {}} />

            <div style={card}>
                <div className="viz-title" style={{ fontSize: 14, fontWeight: 600 }}>Correos del protocolo</div>
                <p style={{ fontSize: 12.5, color: 'var(--c-text-3)', marginTop: 2 }}>
                    Dominios aceptados al agregar destinatarios manuales, y los correos de dirección que reciben todos los reportes.
                </p>

                <div className="ho-kicker" style={{ marginTop: 14 }}>Dominios aceptados</div>
                <div className="d-flex flex-wrap gap-2 mt-2">
                    {(pol.dominiosPermitidos || []).map(d => (
                        <span key={d} className="ho-pill ho-pill-info">
                            {d}
                            <button className="btn btn-link p-0 ms-1" style={{ color: 'inherit', lineHeight: 1 }}
                                    onClick={() => guardar({ dominiosPermitidos: pol.dominiosPermitidos.filter(x => x !== d) })}>×</button>
                        </span>
                    ))}
                </div>
                <div className="d-flex gap-2 mt-2" style={{ maxWidth: 420 }}>
                    <input className="form-control form-control-sm" placeholder="bancaprepa.com" value={dominio}
                           onChange={e => setDominio(e.target.value)}
                           onKeyDown={e => { if (e.key === 'Enter' && dominio.trim()) { guardar({ dominiosPermitidos: [...(pol.dominiosPermitidos||[]), dominio.trim().toLowerCase()] }); setDominio(''); } }} />
                    <button className="ho-chip" onClick={() => { if (dominio.trim()) { guardar({ dominiosPermitidos: [...(pol.dominiosPermitidos||[]), dominio.trim().toLowerCase()] }); setDominio(''); } }}>Agregar</button>
                </div>

                <div className="ho-kicker" style={{ marginTop: 18 }}>Notificaciones a dirección</div>
                <p style={{ fontSize: 12.5, color: 'var(--c-text-3)', margin: '2px 0 0' }}>
                    Estos correos reciben el reporte de <strong>todos</strong> los hechos, sin importar la marca, en copia oculta. Aquí van dirección y RH. Revisa que estén bien escritos: un dominio equivocado y el reporte deja de llegar.
                </p>
                <div className="d-flex flex-wrap gap-2 mt-2">
                    {dirActual.map(email => (
                        <span key={email} className="ho-pill ho-pill-ok">
                            {email}
                            <button className="btn btn-link p-0 ms-1" style={{ color: 'inherit', lineHeight: 1 }}
                                    onClick={() => guardar({ enrutamientoSiempre: dirActual.filter(x => x !== email) })}>×</button>
                        </span>
                    ))}
                    {!dirActual.length && (
                        <span style={{ fontSize: 12.5, color: 'var(--ho-pend)' }}>Sin destinatarios de dirección: nadie recibe copia de todos los hechos.</span>
                    )}
                </div>
                <div className="d-flex gap-2 mt-2" style={{ maxWidth: 420 }}>
                    <input className="form-control form-control-sm" placeholder="recursos.humanos@bancaprepa.com" value={dirCorreo}
                           onChange={e => setDirCorreo(e.target.value)}
                           onKeyDown={e => { if (e.key === 'Enter') agregarDir(); }} />
                    <button className="ho-chip" onClick={agregarDir}>Agregar</button>
                </div>
            </div>
        </>
    );
}

function ListaUsuarios({ titulo, sub, ids, campo, pol, guardar, dic = {} }) {
    // ids llega como array de ObjectId (strings) sin poblar. Los nombres de lo
    // ya guardado vienen en `dic` (usuariosNombres del getConfig); el cache
    // local solo cubre a los recién agregados en esta sesión.
    const [nombres, setNombres] = useState({});
    const datosDe = (id) => nombres[id] || dic[id] || null;

    const quitar = (id) => guardar({ [campo]: (ids || []).filter(x => x !== id) });
    const agregar = (u) => { if (!(ids || []).includes(u._id)) { setNombres(prev => ({ ...prev, [u._id]: u })); guardar({ [campo]: [...(ids || []), u._id] }); } };

    return (
        <div style={card}>
            <div className="viz-title" style={{ fontSize: 14, fontWeight: 600 }}>{titulo}</div>
            <p style={{ fontSize: 12.5, color: 'var(--c-text-3)', marginTop: 2 }}>{sub}</p>
            <div style={{ marginTop: 12 }}><BuscadorUsuario onElegir={agregar} /></div>
            {(ids || []).length > 0 && (
                <div className="d-flex flex-column gap-1 mt-3">
                    {ids.map(id => (
                        <div key={id} className="d-flex align-items-center gap-2" style={{ fontSize: 13.5 }}>
                            <span style={{ color: 'var(--c-text-3)' }}>{I.user}</span>
                            <span>{datosDe(id)?.name || <span className="ho-mono" style={{ fontSize: 12 }}>{id}</span>}</span>
                            {datosDe(id)?.email && <span style={{ fontSize: 12, color: 'var(--c-text-3)' }}>{datosDe(id).email}</span>}
                            <button className="btn btn-link p-0 ms-auto" style={{ color: 'var(--c-text-3)' }} onClick={() => quitar(id)}>{I.x}</button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

// ── Tipos de evento (solo lectura de referencia) ─────────────────────────
function Tipos({ cfg }) {
    return (
        <div style={card}>
            <div className="viz-title" style={{ fontSize: 14, fontWeight: 600 }}>Catálogo de tipos de evento</div>
            <p style={{ fontSize: 12.5, color: 'var(--c-text-3)', marginTop: 2 }}>
                Cada tipo define qué documentos hacen falta para integrar el expediente. Vienen del protocolo oficial.
            </p>
            <table className="viz-tabla" style={{ marginTop: 14 }}>
                <thead><tr><th>Tipo</th><th>Documentos obligatorios</th><th>Severidad</th></tr></thead>
                <tbody>
                    {cfg.tiposEvento.map(t => (
                        <tr key={t._id}>
                            <td>
                                <div style={{ fontWeight: 600 }}>{t.name}</div>
                                {t.description && (
                                    <div style={{ fontSize: 12, color: 'var(--c-text-3)', maxWidth: 460, lineHeight: 1.5 }}>
                                        {t.description}
                                    </div>
                                )}
                            </td>
                            <td style={{ fontSize: 12.5 }}>
                                {t.requiredDocs.map(d => d.label).join(' · ')}
                            </td>
                            <td><Pill tone={t.defaultSeverity === 'alta' ? 'crit' : 'neutro'}>{t.defaultSeverity}</Pill></td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
